import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import PendingRequestTable from "./components/PendingRequestTable";
import RequestDetailModal from "../MeetingRoomRestrictionRequest/components/RequestDetailModal";
import ReservationConflictModal from "./components/ReservationConflictModal";
import RequestApprovalModal from "./components/RequestApprovalModal";
import { useToast } from "hook/useToast";
import RequestRejectModal from "./components/RequestRejectModal";
import ProcessedRequestTable from "./components/ProcessedRequestTable";
import {
  getConflictReservationListByRestrictRequest,
  getWorkspaceRestrictRequestList,
  processedStatusRestrictRequest,
  TRestrictRequest,
} from "apis/meetings/block-request";
import { TReservation } from "apis/meetings/meeting-reservation";
import AlarmManagerSettingTab from "./components/AlarmManagerSettingTab/AlarmManagerSettingTab";

const MeetingRoomRestrictionManagePage = () => {
  const [selectedTab, setSelectedTab] = useState<"승인 대기 요청" | "처리 완료 요청" | "알림/담당자 설정">(
    "승인 대기 요청"
  );
  const [pendingRequestList, setPendingRequestList] = useState<TRestrictRequest[]>([]);
  const [processingRequestList, setProcessingRequestList] = useState<TRestrictRequest[]>([]);
  const [openDetailRequestModal, setOpenDetailRequestModal] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<TRestrictRequest | null>(null);
  const [openReservationConflictModal, setOpenReservationConflictModal] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [openApprovalModal, setOpenApprovalModal] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [conflictReservationList, setConflictReservationList] = useState<TReservation[]>([]);
  const [conflictRestrictionList, setConflictRestrictionList] = useState<TReservation[]>([]);

  const { showToast } = useToast();

  const fetchPendingRequestList = async (page: number) => {
    const res = await getWorkspaceRestrictRequestList(page, "PENDING");
    if (res.success) {
      if (res.data?.blockRequests.length === 0 && page > 1) {
        const res = await getWorkspaceRestrictRequestList(page - 1, "PENDING");
        setPendingRequestList(res.data?.blockRequests ?? []);
        setCurrentPage(res.data?.nowPageNumber ?? 1);
        setTotalPage(res.data?.totalPageNumber ?? 1);
        return;
      }
      setPendingRequestList(res.data?.blockRequests ?? []);
      setCurrentPage(res.data?.nowPageNumber ?? 1);
      setTotalPage(res.data?.totalPageNumber ?? 1);
    }
  };

  const fetchCompletedRequestList = async (page: number) => {
    const res = await getWorkspaceRestrictRequestList(page, "COMPLETED");
    if (res.success) {
      setProcessingRequestList(res.data?.blockRequests ?? []);
      setCurrentPage(res.data?.nowPageNumber ?? 1);
      setTotalPage(res.data?.totalPageNumber ?? 1);
    }
  };

  useEffect(() => {
    if (selectedTab === "승인 대기 요청") fetchPendingRequestList(1);
    if (selectedTab === "처리 완료 요청") fetchCompletedRequestList(1);
  }, [selectedTab]);

  return (
    <div
      style={{
        minWidth: 1140,
        display: "flex",
        flexDirection: "column",
        padding: "0px 20px 80px 20px",
        gap: 24,
      }}
    >
      <div className="upperSection" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{ padding: "24px 20px" }}
        >
          예약 제한 요청 관리
        </UI.Typography>
        <div
          className="tabBarContainer"
          style={{
            display: "flex",
            padding: "0 24px",
            gap: 20,
            borderBottom: "1px solid var(--alias-color-line-normal",
          }}
        >
          <div
            className="tabItem"
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              borderBottom: selectedTab === "승인 대기 요청" ? "2px solid var(--alias-color-primary-normal)" : "",
            }}
            onClick={() => setSelectedTab("승인 대기 요청")}
          >
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color={
                selectedTab === "승인 대기 요청"
                  ? "var(--alias-color-primary-normal)"
                  : "var(--alias-color-label-alternative)"
              }
            >
              승인 대기 요청
            </UI.Typography>
          </div>
          <div
            className="tabItem"
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              borderBottom: selectedTab === "처리 완료 요청" ? "2px solid var(--alias-color-primary-normal)" : "",
            }}
            onClick={() => setSelectedTab("처리 완료 요청")}
          >
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color={
                selectedTab === "처리 완료 요청"
                  ? "var(--alias-color-primary-normal)"
                  : "var(--alias-color-label-alternative)"
              }
            >
              처리 완료 요청
            </UI.Typography>
          </div>
          <div
            className="tabItem"
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              borderBottom: selectedTab === "알림/담당자 설정" ? "2px solid var(--alias-color-primary-normal)" : "",
            }}
            onClick={() => setSelectedTab("알림/담당자 설정")}
          >
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color={
                selectedTab === "알림/담당자 설정"
                  ? "var(--alias-color-primary-normal)"
                  : "var(--alias-color-label-alternative)"
              }
            >
              알림 / 담당자 설정
            </UI.Typography>
          </div>
        </div>
      </div>
      <div
        className="bottomSection"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 40,
          padding: selectedTab === "알림/담당자 설정" ? 0 : "0 20px",
        }}
      >
        {selectedTab === "승인 대기 요청" && (
          <PendingRequestTable
            data={pendingRequestList}
            onClickItem={(item: TRestrictRequest) => {
              setSelectedRequest(item);
              setOpenDetailRequestModal(true);
            }}
            onApproval={async (item: TRestrictRequest) => {
              setSelectedRequest(item);
              if (!item) return;
              const res = await getConflictReservationListByRestrictRequest(item.id);
              setConflictReservationList(res.data?.meetingReservations ?? []);
              setConflictRestrictionList(res.data?.meetingRestrictions ?? []);
              setOpenReservationConflictModal(true);
            }}
            onReject={(item: TRestrictRequest) => {
              setSelectedRequest(item);
              setOpenRejectModal(true);
            }}
          />
        )}
        {selectedTab === "처리 완료 요청" && (
          <ProcessedRequestTable
            data={processingRequestList}
            onClickItem={(item: TRestrictRequest) => {
              setSelectedRequest(item);
              setOpenDetailRequestModal(true);
            }}
          />
        )}
        {((selectedTab === "승인 대기 요청" && pendingRequestList.length !== 0) ||
          (selectedTab === "처리 완료 요청" && processingRequestList.length !== 0)) && (
          <UI.Pagination
            pageCount={totalPage}
            currentPage={currentPage}
            onChange={(page) => {
              if (selectedTab === "승인 대기 요청") fetchPendingRequestList(page);
              else fetchCompletedRequestList(page);
            }}
          />
        )}
        {selectedTab === "알림/담당자 설정" && <AlarmManagerSettingTab isShow={selectedTab === "알림/담당자 설정"} />}
      </div>
      <RequestDetailModal
        modalIsOpen={openDetailRequestModal}
        closeModal={() => setOpenDetailRequestModal(false)}
        data={selectedRequest ?? undefined}
        onApproval={async () => {
          if (!selectedRequest) return;
          const res = await getConflictReservationListByRestrictRequest(selectedRequest.id);
          setConflictReservationList(res.data?.meetingReservations ?? []);
          setConflictRestrictionList(res.data?.meetingRestrictions ?? []);
          setOpenReservationConflictModal(true);
        }}
        onReject={() => {
          setOpenRejectModal(true);
        }}
      />
      <ReservationConflictModal
        modalIsOpen={openReservationConflictModal}
        closeModal={() => setOpenReservationConflictModal(false)}
        conflictReservationData={conflictReservationList}
        conflictRestrictionData={conflictRestrictionList}
        onRestrict={() => {
          setOpenReservationConflictModal(false);
          setOpenApprovalModal(true);
        }}
      />
      <RequestApprovalModal
        modalIsOpen={openApprovalModal}
        closeModal={() => setOpenApprovalModal(false)}
        data={selectedRequest || undefined}
        onApproval={async (reason) => {
          if (!selectedRequest) return;
          const res = await processedStatusRestrictRequest(selectedRequest?.id, "APPROVED", reason);
          if (res.success) {
            showToast("예약 제한이 성공적으로 승인되었습니다.", "success");
            setOpenApprovalModal(false);
            setOpenDetailRequestModal(false);
            fetchPendingRequestList(currentPage);
          } else showToast("예약 제한 승인에 슬패했습니다. 다시 시도해 주세요.", "error");
        }}
      />
      <RequestRejectModal
        modalIsOpen={openRejectModal}
        closeModal={() => setOpenRejectModal(false)}
        data={selectedRequest || undefined}
        onReject={async (reason) => {
          if (!selectedRequest) return;
          const res = await processedStatusRestrictRequest(selectedRequest.id, "REJECTED", reason);
          if (res.success) {
            showToast("예약 제한 요청이 반려되었습니다.", "success");
            setOpenRejectModal(false);
            setOpenDetailRequestModal(false);
            fetchPendingRequestList(currentPage);
          } else {
            showToast("반려 처리에 실패했습니다. 다시 시도해 주세요.", "error");
          }
        }}
      />
    </div>
  );
};

export default MeetingRoomRestrictionManagePage;
